<script lang="ts">
  import * as Dialog from "$lib/components/ui/dialog/index.js";
  import { Button, buttonVariants } from "$lib/components/ui/button/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import { Label } from "$lib/components/ui/label/index.js";
  import { t } from "svelte-i18n";
  import * as zod from "zod";
  import { createForm } from "felte";
  import { validator } from "@felte/validator-zod";
  import { ScrollArea } from "$lib/components/ui/scroll-area/index.js";
  import { cn } from "$lib/utils";
  import { useCreateCustomer } from "../hooks/useCreateCustomer";
  import { createEventDispatcher } from "svelte";
  import ErrorDialog from "$src/presentation/defaults/ErrorDialog.svelte";
  import ZodTranslatedMessage from "$src/presentation/core/ZodTranslatedMessage.svelte";
  import { type Contact, contactSchema } from "$src/domain/customers/Contact";
  import { Pencil, Plus, Trash2 } from "lucide-svelte";

  export let editMode: boolean = false;

  export let contact: Contact | undefined = undefined;

  let isOpen: boolean = false;

  const dispatch = createEventDispatcher();

  type FormValues = zod.infer<typeof contactSchema>;

  $: editForm = createForm({
    initialValues: {
      id: -1,
      firstname: contact?.firstname,
      lastname: contact?.lastname,
      email: contact?.email,
      phoneNumber1: contact?.phoneNumber1
    },
    onSubmit: handleSubmit,
    extend: validator({ schema: contactSchema }),
  });

  $: form = editForm.form;
  $: data = editForm.data;
  $: errors = editForm.errors;

  let lastnameInput: Input;

  async function handleSubmit(values: FormValues) {
    if( editMode && contact) {
        contact.firstname = values.firstname;
        contact.lastname = values.lastname;
        contact.email = values.email;
        contact.phoneNumber1 = values.phoneNumber1;

        dispatch('contactUpdated', contact);
    }
    else {
        let newContact: Contact = {
            id: -1,
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
            phoneNumber1: values.phoneNumber1,
        }

        dispatch('contactCreated', newContact);
    }
    isOpen = false;
  }

  function deleteContact() {
    dispatch('contactDeleted', contact);
    isOpen = false;
  }
</script>

<Dialog.Root bind:open={isOpen}>
  <Dialog.Trigger
    class={cn(
      buttonVariants({ variant: "default" }),
      "rounded-full p-0  h-9 w-9 transition-transform transform  hover:scale-110",
      editMode ? " absolute right-2 bottom-2" : ""
    )}
  >
    {#if editMode}
      <Pencil size="18" />
    {:else}
      <Plus size="18" />
    {/if}
  </Dialog.Trigger>
  <Dialog.Content
    class="flex flex-col h-dvh sm:h-[650px] sm:my-auto overflow-hidden sm:max-h-dvh"
  >
    <Dialog.Header>
      {#if editMode}
        <Dialog.Title>{$t("page.customers.new.edit-contact")}</Dialog.Title>
      {:else}
        <Dialog.Title>{$t("page.customers.new.contact")}</Dialog.Title>
      {/if}
    </Dialog.Header>
    <form use:form class="flex flex-col justify-between h-full overflow-hidden">
      <div class="flex flex-col flex-grow h-full overflow-hidden">
        <ScrollArea orientation="vertical">
          <div class="grid w-full h-full grid-cols-2 gap-2 max-w-md p-2">
            <div class="col-span-full flex items-center flex-wrap">
              <Label class="text-xs text-muted-foreground">
                {$t("page.customers.labels.lastname")}
              </Label>
              <Input
                bind:this={lastnameInput}
                name="lastname"
                class={cn(
                  "col-span-3",
                  $errors.lastname ? "border-red-500" : ""
                )}
              />
              {#if $errors.lastname}
                <div class="text-xs text-red-500 w-full text-right">
                  <ZodTranslatedMessage messages={$errors.lastname} />
                </div>
              {/if}
            </div>
            <div class="col-span-full flex items-center flex-wrap">
              <Label for="firstname" class="text-xs text-muted-foreground">
                {$t("page.customers.labels.firstname")}
              </Label>
              <Input
                name="firstname"
                bind:value={$data.firstname}
                class={cn(
                  "col-span-3",
                  $errors.firstname ? "border-red-500" : ""
                )}
              />
              {#if $errors.firstname}
                <div class="text-xs text-red-500 w-full text-right">
                  <ZodTranslatedMessage messages={$errors.firstname} />
                </div>
              {/if}
            </div>

            <div class="col-span-full flex items-center flex-wrap">
              <Label for="phoneNumber1" class="text-xs text-muted-foreground">
                {$t("page.customers.labels.phoneNumber1")}
              </Label>
              <Input
                name="phoneNumber1"
                type="phone"
                bind:value={$data.phoneNumber1}
                class={cn(
                  "col-span-3",
                  $errors.phoneNumber1 ? "border-red-500" : ""
                )}
              />
              {#if $errors.phoneNumber1}
                <div class="text-xs text-red-500 w-full text-right">
                  <ZodTranslatedMessage messages={$errors.phoneNumber1} />
                </div>
              {/if}
            </div>
            <div class="col-span-full flex items-center flex-wrap">
              <Label for="email" class="text-xs text-muted-foreground">
                {$t("page.customers.labels.email")}
              </Label>
              <Input
                name="email"
                type="email"
                bind:value={$data.email}
                class={cn("col-span-3", $errors.email ? "border-red-500" : "")}
              />
              {#if $errors.email}
                <div class="text-xs text-red-500 w-full text-right">
                  <ZodTranslatedMessage messages={$errors.email} />
                </div>
              {/if}
            </div>
          </div>
        </ScrollArea>
      </div>
      <Dialog.Footer>
        <div class="flex flex-1 flex-row justify-between gap-2">
          <div class="flex-shrink flex gap-2">
            {#if editMode}
              <Button variant="destructive" class="gap-2" on:click={deleteContact}>
                <Trash2 class="h-4 w-4" />
              </Button>
            {/if}
          </div>
          <div>
            {#if editMode}
              <Button type="submit" class="px-8">
                {$t("global.messages.edit")}
              </Button>
            {:else}
              <Button type="submit" class="px-8">
                {$t("global.messages.create")}
              </Button>
            {/if}
            <Button
             variant="ghost"
              class="px-8"
              on:click={() => {
                isOpen = false;
              }}
            >
              {$t("global.messages.cancel")}
            </Button>
          </div>
        </div>
      </Dialog.Footer>
    </form>
  </Dialog.Content>
</Dialog.Root>
